import Api from "@/services/Api";
import Vue from "vue";
import VueCroppie from "vue-croppie";
import VueI18n from "vue-i18n";
import VueLazyload from "vue-lazyload";
import VueSweetAlert from "vue-sweetalert2";
import * as VueGoogleMaps from "vue2-google-maps";
import { sync } from "vuex-router-sync";
import App from "./App.vue";
import vuetify from "./plugins/vuetify";
import router from "./router";
import sentry from "./sentry";
import { Environment } from "./services/Environment";
import store from "./store";

import ImageDisplay from "@/components/displays/ImageDisplay";

import FlowchartBlock from "@/components/flowcharts/FlowchartBlock";
import FlowchartNode from "@/components/flowcharts/FlowchartNode";
import FlowchartNodeView from "@/components/flowcharts/FlowchartNodeView";
import FlowyPlugin from "@hipsjs/flowy-vue";

//CSS
import "@hipsjs/flowy-vue/dist/lib/flowy-vue.css";

//CSS
import "@/assets/styles/main.css";
import "croppie/croppie.css";
import "sweetalert2/dist/sweetalert2.min.css";

const moment = require("moment");

//Keep store and router in sync
sync(store, router);

Vue.config.productionTip = false;

//Translations
import t_cz from "@/assets/locale/translations_cz.json";
import t_de from "@/assets/locale/translations_de.json";
import t_en from "@/assets/locale/translations_en.json";
import t_es from "@/assets/locale/translations_es.json";
import t_fr from "@/assets/locale/translations_fr.json";
import t_it from "@/assets/locale/translations_it.json";
import t_nl from "@/assets/locale/translations_nl.json";
import t_pt from "@/assets/locale/translations_pt.json";
import t_ru from "@/assets/locale/translations_ru.json";

import "@/assets/styles/vue-tour-override.css";
import VueTour from "vue-tour";

require("vue-tour/dist/vue-tour.css");

Vue.use(VueTour);

Vue.use(VueI18n);
Vue.use(VueLazyload);
Vue.use(VueSweetAlert);
Vue.use(VueCroppie);
Vue.use(FlowyPlugin);
Vue.use(VueGoogleMaps, {
  load: {
    key: "AIzaSyDS7qZboGXsPMwSUxk35eRX7pDu26mU-MI",
    libraries: "places",
  },
});

const messages = {
  en: t_en,
  es: t_es,
  pt: t_pt,
  fr: t_fr,
  cz: t_cz,
  de: t_de,
  it: t_it,
  nl: t_nl,
  ru: t_ru,
};

//Directive to manage permissions in a ver custom way
Vue.directive("allowed", {
  // When the bound element is inserted into the DOM...
  inserted: function (el, binding, vnode) {
    // Focus the element
    const permissions = vnode.context.$store.state.permissions;
    //console.log("User permissions: ", permissions);
    //console.log("Against [", binding.value + "]");
    if (permissions) {
      if (permissions.length == 0) {
        //No permissions but trying to access a root required
        if (binding.value == "root")
          vnode.elm.parentElement.removeChild(vnode.elm);
      } else if (permissions.indexOf("root") < 0) {
        //Not a root user...
        if (permissions.indexOf(binding.value) < 0) {
          //console.log("WE ARE HIDING");
          vnode.elm.parentElement.removeChild(vnode.elm);
        } //else console.log("We good, user has permission: ", binding.value);
      }
    }
  },
});

const ASSETS_URL = Environment.get("VUE_APP_ASSETS_URL");

Vue.mixin({
  data() {
    return {
      searchesLimit: 20,
      fromRegistration: false,
      browserLang: this.getBrowserLang(),
      validationRules: {
        number: (value) => {
          const pattern = /^\d+$/;
          return pattern.test(value) || this.$t("message.not_a_number");
        },
        decimal: (value) => {
          const pattern = /^\d*\.?\d*$/;
          return pattern.test(value) || this.$t("message.not_a_number");
        },
        email: (value) => {
          const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(value) || this.$t("message.invalid");
        },
      },
    };
  },
  methods: {
    longTimeout(minutes = 2) {
      return minutes * 60 * 1000;
    },
    modulesAllowed(modules, key) {
      //modules 'federation' should return true for academy-page, academies, growth
      if (
        modules.indexOf("federations") &&
        ["academies", "academy", "growth-central", "documents"].includes(key)
      ) {
        return true;
      } else if (modules.indexOf("all") && key != "academies") {
        return true;
      } else {
        return false;
      }
    },
    hasCommonElement(list1, list2) {
      return list1.some((item) => list2.includes(item));
    },
    cleanAndFormatFilename(filename, type) {
      // Extract the file extension
      const extensionIndex = filename.lastIndexOf(".");
      if (extensionIndex === -1) {
        throw new Error("Invalid filename: No extension found.");
      }
      const d = new Date();
      const millis = d.getTime();
      const extension = filename.slice(extensionIndex); // Extension with the dot
      return (
        "uploaded-" +
        (type ? type : "image") +
        "-user-admin-" +
        millis +
        extension
      );
    },
    doNothing() {},
    expiredPaymentMethod(paymentMethod) {
      if (paymentMethod && paymentMethod.expYear) {
        let expDate = new Date();
        expDate.setFullYear(paymentMethod.expYear);
        expDate.setMonth(paymentMethod.expMonth - 1);
        paymentMethod.expDate = expDate;
        let today = new Date();
        if (expDate > today) return false;
        else return true;
      }
    },
    copyToClipboard(text) {
      const vm = this;
      navigator.clipboard.writeText(text).then(
        function () {
          vm.showAlert("success", vm.$t("message.copied"));
        },
        function () {
          /* clipboard write failed */
          vm.showAlert("error", vm.$t("message.error_generic"));
        },
      );
    },
    getAvailabeEmailTokens() {
      return [
        { text: this.$t("message.user_name"), value: "{USER_NAME}" },
        { text: this.$t("message.academy_name"), value: "{ACADEMY_NAME}" },
        //Only substituted by the expiring soon reminder. A template that uses
        //them elsewhere keeps the token text as-is rather than blanking it.
        {
          text: this.$t("message.membership_name"),
          value: "{MEMBERSHIP_NAME}",
        },
        {
          text: this.$t("message.expiration_date"),
          value: "{EXPIRATION_DATE}",
        },
        { text: this.$t("message.amount"), value: "{AMOUNT}" },
      ];
    },
    toBeginningOfDay(d) {
      let date = d ? new Date(d) : new Date();
      if (isNaN(date.getTime())) date = new Date();
      date.setHours(0);
      date.setMinutes(0);
      date.setSeconds(0);
      return date;
    },
    toBeginningOfMonth(dateInput) {
      let d = new Date();
      if (dateInput) d = new Date(dateInput);
      d.setDate(1);
      d.setHours(0);
      d.setMinutes(0);
      d.setSeconds(0);
      d.setMilliseconds(0);
      return d;
    },
    isValidEmail(email) {
      let regex =
        /^([a-zA-Z0-9_.+-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;
      return regex.test(email);
    },
    isLateCheckin(checkinTime, classStartTime) {
      const date1 = new Date(checkinTime);
      date1.setSeconds(0);
      const date2 = new Date();
      const time = this.numberToHoursAndMinutes(classStartTime);
      if (!time) return null;
      date2.setDate(date1.getDate());
      date2.setHours(time.hours);
      date2.setMinutes(time.minutes);
      date2.setSeconds(0);

      const timestamp1 = date1.getTime();
      const timestamp2 = date2.getTime();

      // If the first date is before or the same as the second, return null.
      if (timestamp1 <= timestamp2) {
        //console.log("[" + date1 + "] < " + date2 + " - ON TIME");
        return null;
      }

      // Otherwise, calculate the difference in milliseconds.
      const differenceInMilliseconds = timestamp1 - timestamp2;

      // Convert the milliseconds to minutes.
      const differenceInMinutes = Math.floor(
        differenceInMilliseconds / (1000 * 60),
      );

      //console.log("[" + date1 + "] > " + date2 + " - LATE");
      return differenceInMinutes;
    },
    toEndOfDay(d) {
      let date = d ? new Date(d) : new Date();
      if (isNaN(date.getTime())) date = new Date();
      date.setHours(23);
      date.setMinutes(59);
      date.setSeconds(50);
      return date;
    },
    getMonthRange(monthsBack) {
      const now = new Date();

      // Create a copy for the past date
      const past = new Date();

      // 1. Move back X months
      past.setMonth(now.getMonth() - monthsBack);

      // 2. Reset to the beginning of that month
      past.setDate(1);
      past.setHours(0, 0, 0, 0);

      return {
        to: now,
        from: past,
      };
    },
    themeColor(color) {
      let theme = this.$store.state.theme;
      let themeColor = this.$vuetify.theme.themes[theme][color];
      if (themeColor) return themeColor;
      else return this.$vuetify.theme.themes.light.primary;
    },
    assets(file) {
      if (file) return ASSETS_URL + "/" + file;
      else return ASSETS_URL;
    },
    getDiscountedPrice(originalAmount, discountPercent, discountAmount) {
      if (originalAmount) {
        if (discountAmount) {
          return originalAmount - discountAmount;
        } else {
          const numVal2 = discountPercent / 100;
          const decimal = originalAmount - originalAmount * numVal2;
          const with2Decimals = decimal
            .toString()
            .match(/^-?\d+(?:\.\d{0,2})?/)[0];
          return new Number(with2Decimals).valueOf();
        }
      }
    },
    isCustomAchTransaction(transaction) {
      if (transaction) {
        if (
          transaction.paymentType == "acss_debit" ||
          transaction.paymentType == "nz_becs_debit" ||
          transaction.paymentType == "becs_debit"
        )
          return true;
      }
      return false;
    },
    getSubscriptionStatusText(status) {
      let text = "";
      if (status) {
        if (status == "pending_payment")
          text = this.$t("message.pending_payment");
        else if (status == "waiting_confirmation")
          text = this.$t("message.waiting_confirmation");
        else if (status == "active") text = this.$t("message.active");
        else if (status == "unpaid") text = this.$t("message.unpaid");
        else if (status == "past_due") text = this.$t("message.past_due");
        else if (status.indexOf("expired") >= 0)
          text = this.$t("message.expired");
        else if (status == "paused") text = this.$t("message.paused");
        else text = status;
      }
      return text;
    },
    getAvailableTokens() {
      return [
        { text: this.$t("message.user_name"), value: "{USER_NAME}" },
        { text: this.$t("message.academy_name"), value: "{ACADEMY_NAME}" },
      ];
    },
    generatePassword(length = 8) {
      const chars =
        "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      let password = "";
      for (let i = 0; i < length; i++) {
        password += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return password;
    },
    getRandomColorVariation(hex, variation = 30) {
      // Ensure the hex starts with #
      if (!/^#?[0-9A-Fa-f]{6}$/.test(hex)) {
        throw new Error("Invalid hex color format");
      }

      // Remove # if present
      hex = hex.replace("#", "");

      // Convert hex to RGB
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);

      // Apply random variation to each channel
      const vary = (channel) => {
        const change =
          Math.floor(Math.random() * (2 * variation + 1)) - variation;
        return Math.max(0, Math.min(255, channel + change));
      };

      const rVar = vary(r);
      const gVar = vary(g);
      const bVar = vary(b);

      // Convert back to hex with leading zeros
      const toHex = (value) => value.toString(16).padStart(2, "0");

      return `#${toHex(rVar)}${toHex(gVar)}${toHex(bVar)}`;
    },
    getStatusColor(t, status) {
      const cancelled = t.cancelled;
      const expired = t.expired;
      if (t && t.paymentsModule == "easypay" && t.subscriptionStartDate) {
        const now = new Date();
        const futureDate = new Date(t.subscriptionStartDate);
        futureDate.setHours(now.getHours());
        if (futureDate > now) {
          return "#FFD700";
        }
      }
      if (!status && t.subscription && t.subscription.status)
        status = t.subscription.status;
      if (status) {
        return this.getTransactionStatusColor(status);
      } else if (t.status && t.status != "pending_payment") {
        if (expired) return this.themeColor("error");
        else return this.getTransactionStatusColor(t.status);
      } else if (t) {
        if (!t._id) {
          //Passing the status itself
          if (t == "active") return "green";
          else if (t == "expired") return "red";
          else if (t == "pending_payment") return "#FFD700";
          else return "#FFD700";
        } else {
          const now = new Date();
          if (cancelled || expired) {
            return this.themeColor("error");
          } else if (t.completed && t.pausedUntil) {
            return "#FFD700";
          } else if (t.completed && t.subscriptionStartDate) {
            try {
              const startDate = new Date(t.subscriptionStartDate);
              if (startDate > now) return "#FFD700";
              else return this.themeColor("success");
            } catch (err) {
              return this.themeColor("error");
            }
          } else if (t.completed) {
            return this.themeColor("success");
          } else {
            return this.themeColor("error");
          }
        }
      }
    },
    isBJJLinkNextPaymentModule(paymentsModule) {
      const BJJLINK_NEXT_MANAGED_PAYMENT_MODULES = ["infinicept"];
      return !!(
        paymentsModule &&
        BJJLINK_NEXT_MANAGED_PAYMENT_MODULES.includes(paymentsModule)
      );
    },
    getTransactionStatusColor(status, t) {
      if (t) {
        if (t.pauseDate) return "#FFD700";
        else if (t.cancellationDate) return this.themeColor("error");
      }
      if (status == "active") return this.themeColor("success");
      else if (status == "completed") return this.themeColor("success");
      else if (status == "succeeded") return this.themeColor("success");
      else if (status == "success") return this.themeColor("success");
      else if (status == "pending_payment") return this.themeColor("error");
      else if (status && status.indexOf("expired") >= 0)
        return this.themeColor("error");
      else if (status == "paused") return this.themeColor("yellow");
      else if (status == "trialing") return this.themeColor("yellow");
      else if (status == "open") return this.themeColor("error");
      else if (status == "past_due") return this.themeColor("error");
      else return this.themeColor("error");
    },
    isExpired(expirationDate) {
      if (expirationDate) {
        let today = new Date();
        today.setDate(1);
        today.setMonth(today.getMonth() + 1);
        today.setHours(0);
        today.setMinutes(0);
        //Today is end of this month
        if (expirationDate > today) return false;
        else return true;
      }
      return false;
    },
    getTransactionStatusText(status) {
      if (status == "active") return this.$t("message.active");
      else if (status == "succeeded" || status == "success")
        return this.$t("message.success");
      else if (status == "pending_payment")
        return this.$t("message.pending_payment");
      else if (status == "paused") return this.$t("message.paused");
      else if (status == "trialing")
        return this.$t("message.subscription_status_trialing");
      else if (status == "open") return this.$t("message.payment_status_open");
      else if (status == "past_due")
        return this.$t("message.subscription_status_past_due");
      else if (status.indexOf("expired") >= 0)
        return this.$t("message.expired");
      else if (status.indexOf("requires_payment_method") >= 0)
        return this.$t("message.failed");
      // BJJLINK-managed recurring modules (infinicept) store one of these
      // on the transaction directly instead of a Stripe-style status.
      else if (status == "completed") return this.$t("message.completed");
      else if (status == "inprogress") return this.$t("message.processing");
      else if (status == "cancelled") return this.$t("message.cancelled");
      else if (status == "failed") return this.$t("message.failed");
      else return "unknown";
    },
    getCurrentZValue() {
      let d = new Date().toISOString();
      return d.split(".")[1];
    },
    decimalToHoursAndMinutes(n) {
      if (!n && n !== 0) return null;
      const fixed = parseFloat(n).toFixed(2);
      const [h, m] = fixed.split(".");
      return { hours: parseInt(h, 10), minutes: parseInt(m || "0", 10) };
    },
    getTransactionTotal(t) {
      if (t.coupon && !isNaN(t.coupon.discountedTotal)) {
        if (t.membershipAmount) return t.membershipAmount;
        else return t.coupon.discountedTotal;
      } else {
        const q = t.quantity;
        if (t.membershipAmount) {
          return t.membershipAmount.toFixed(2);
        } else {
          const a = t.amount
            ? t.amount
            : t.product
            ? t.product.amountDecimal
            : 0;
          if (a > 0) return (q * a).toFixed(2);
          else return 0;
        }
      }
    },
    getManualCheckinClassData(schedule, classId, customClasses) {
      let classData = null;
      let duration = 0;
      let customClass = false;
      let checkinTime = new Date();
      if (customClasses && customClasses[classId]) {
        //checkinTime = new Date(classData.start);
        const data = customClasses[classId];
        const d = new Date(data.date);
        classData = {
          checkinTime: data.date,
          professor: data.professorUser,
          name: data.title,
          day: this.getDayString(d),
          start: data.startNumber,
          end: data.endNumber,
          professorUser: data.professorUser,
          _id: data.id,
        };
        customClass = true;
      } else if (schedule && classId) {
        const days = Object.keys(schedule);
        if (days && days.length > 0) {
          for (const day of days) {
            let dayClasses = this.schedule[day];
            for (const c of dayClasses) {
              if (c._id == classId) {
                classData = c;
                break;
              }
            }
          }
        }
      }
      if (classData) {
        //Duration
        let start = classData.start;
        let end = classData.end;
        if (end) {
          const toMin = (t) => {
            const [h, m] = parseFloat(t).toFixed(2).split(".");
            return parseInt(h, 10) * 60 + parseInt(m || "0", 10);
          };
          duration = toMin(end) - toMin(start);
        }
        if (customClass) {
          checkinTime = new Date(classData.checkinTime);
        } else {
          const todayDayOfWeek = checkinTime.getDay() + 1; //We dont want to deal with 0
          const classDayOfWeek = this.dayToNumber(classData.day) + 1; //We dont want to deal with 0
          if (todayDayOfWeek != classDayOfWeek) {
            //console.log("todayDayOfWeek ", todayDayOfWeek);
            //console.log("classDayOfWeek: ", classDayOfWeek);
            let daysBetween = (todayDayOfWeek - classDayOfWeek) * -1;
            //console.log("daysBetween1: ", daysBetween);
            if (daysBetween > 0) {
              //We are past the time
              daysBetween = (7 - daysBetween) * -1;
            }
            //console.log(checkinTime);
            //console.log("daysBetween2: ", daysBetween);
            checkinTime.setDate(checkinTime.getDate() + daysBetween);
          }
          const time = this.numberToHoursAndMinutes(start);
          checkinTime.setHours(time.hours);
          checkinTime.setMinutes(time.minutes);
        }
      }
      return {
        duration: duration,
        customClass: customClass,
        class: classData,
        checkinTime: checkinTime,
        professorUser: classData ? classData.professorUser : null,
        assistantProfessors: classData ? classData.assistantProfessors : null,
      };
    },
    dateFromString(dateString) {
      //Expected format: 2021-04-23T13:00:00.000Z
      let d = null;
      if (dateString && dateString != "") {
        const split = dateString.toString().split("-");
        if (split.length <= 2) {
          return new Date(dateString);
        }
        try {
          let split = dateString.split("T");
          const time = split[1].split(":");
          const date = split[0].split("-");
          if (date && date.length == 3 && time && time.length == 3) {
            const zVal = this.getCurrentZValue();
            const adjustedDateString = dateString.split(".")[0] + "." + zVal;
            d = new Date(adjustedDateString);
            d.setUTCFullYear(date[0]);
            //console.log("Year: ", date[0]);
            const monthNumber = new Number(date[1]).valueOf() - 1;
            d.setUTCMonth(monthNumber);
            //console.log("Month: ", monthNumber);
            d.setUTCDate(date[2]);
            //console.log("Date: ", date[2]);
            const hoursNumber = new Number(time[0]).valueOf();
            d.setUTCHours(hoursNumber);
            //console.log("Hours: ", hoursNumber);
            const minutesNumber = new Number(time[1]).valueOf();
            d.setUTCMinutes(minutesNumber);
            //console.log("Min: ", minutesNumber);
            d.setUTCSeconds(0);
            //console.log("From[" + dateString + "] to: ", d);
            d.setUTCMilliseconds(0);
            //console.log("New date from string: ", d);
            return d;
          } else {
            return new Date();
          }
        } catch (err) {
          return new Date();
        }
      }
      return new Date();
    },
    dateFromStringInTZ(dateString) {
      //Expected format: 2021-04-23T13:00:00.000Z
      let d = null;
      if (dateString && dateString != "") {
        const split = dateString.toString().split("-");
        if (split.length <= 2) {
          return new Date(dateString);
        }
        try {
          let split = dateString.split("T");
          const time = split[1].split(":");
          const date = split[0].split("-");
          if (date && date.length == 3 && time && time.length == 3) {
            const zVal = this.getCurrentZValue();
            const adjustedDateString = dateString.split(".")[0] + "." + zVal;
            d = new Date(adjustedDateString);
            d.setFullYear(date[0]);
            //console.log("Year: ", date[0]);
            const monthNumber = new Number(date[1]).valueOf() - 1;
            d.setMonth(monthNumber);
            //console.log("Month: ", monthNumber);
            d.setDate(date[2]);
            //console.log("Date: ", date[2]);
            const hoursNumber = new Number(time[0]).valueOf();
            d.setHours(hoursNumber);
            //console.log("Hours: ", hoursNumber);
            const minutesNumber = new Number(time[1]).valueOf();
            d.setMinutes(minutesNumber);
            //console.log("Min: ", minutesNumber);
            d.setSeconds(0);
            //console.log("From[" + dateString + "] to: ", d);
            d.setMilliseconds(0);
            //console.log("New date from string: ", d);
            return d;
          } else {
            "Invalid date string. Actual date: " +
              dateString +
              ". Expected: yyyy-mm-ddThh:mm:ss.000Z";
          }
        } catch (err) {
          return d;
        }
      }
      return d;
    },
    updateNotificationsCount() {
      Api()
        .get(
          "admins/academy/" +
            this.$store.state.managedAcademy +
            "/notifications",
        )
        .then((response) => {
          if (response && response.data)
            this.$store.dispatch(
              "setNotificationsCount",
              response.data.totalCount,
            );
        });
    },
    logActivity(data) {
      if (data && data.activityType) {
        data["source"] = "admin";
        if (this.$store.state.managedAcademy)
          data["managedAcademy"] = this.$store.state.managedAcademy;
        Api().post("users/activity/" + data.activityType, data);
      }
    },
    timeStringToFloat(time) {
      var hoursMinutes = time.split(/[.:]/);
      var hours = parseInt(hoursMinutes[0], 10);
      var minutes = hoursMinutes[1] ? parseInt(hoursMinutes[1], 10) : 0;
      return parseFloat(hours + "." + String(minutes).padStart(2, "0"));
    },
    string2Decimal(s) {
      const amount = s + "";
      const price = amount.substring(0, amount.length - 2);
      const priceDecimal = amount.substring(amount.length - 2, amount.length);
      const num = new Number(price + "." + priceDecimal);
      if (isNaN(num)) return s;
      else return num;
    },
    unixString2Datetime(s) {
      if (s) {
        if (isNaN(s) && s.indexOf("-") > 0) return new Date(s);
        //Its an actual date
        else return new Date(new Number(s) * 1000);
      } else return new Date();
    },
    goToProfile() {
      //window.open("https://bjjlink.com/secure/user/" + id);
      //console.log("Do noting.");
    },
    goToMember(id, crumbName) {
      if (!id) return;
      this.navigateTo({
        name: "viewmember",
        params: { memberid: id },
        crumbName: crumbName,
      });
    },
    displayBeltDay(d) {
      if (d) {
        try {
          let date = this.dateFromString(d);
          return this.$d(date, "month_day_year", this.$i18n.locale);
        } catch (e) {
          return d;
        }
      }
    },
    goToVisitor(id, name) {
      if (!id) return;
      this.navigateTo({
        name: "viewvisitor",
        params: { visitorId: id },
        crumbName: name || null,
      });
    },
    getIntervalOptions() {
      return [
        { text: this.$t("message.today"), value: 0 },
        { text: this.$t("message.last7days"), value: 1 },
        { text: this.$t("message.last30days"), value: 2 },
        { text: this.$t("message.last6months"), value: 3 },
        { text: this.$t("message.last_year"), value: 4 },
        //{ text: this.$t("message.this_month"), value: 4 },
      ];
    },
    getIntervalOptionText(num) {
      if (num == 2) return this.$t("message.last30days");
      else if (num == 3) return this.$t("message.this_week");
      else if (num == 4) return this.$t("message.this_month");
      else return this.$t("message.last7days");
    },
    isAppAdmin(userId) {
      const bjjlinkAdmins = [
        "5becfc760a3a3a09912cb546",
        "5befa9c192698d06ab415071",
        "5bf1b1a849808205ba15039a",
        "5ad5642fb5883f5b2fed7fac",
        "5bf0448692698d06ab415088",
        "5c0efc082e9ce106857709f0",
        "5c00956df58c0a1c27aba5f2",
        "5cad890845ec963c3217df93",
        "5bf2a56849808205ba150404", //Antonio
        "5bf2df57f52e110e89a417d3", //Diego Caballero
      ];
      if (
        !userId &&
        this.$store &&
        this.$store.state.user &&
        bjjlinkAdmins.indexOf(this.$store.state.user._id.toString()) >= 0
      )
        return true;
      else if (bjjlinkAdmins.indexOf(userId) >= 0) return true;
      else return false;
    },
    isSystemAdmin(userId) {
      const bjjlinkAdmins = [
        "5becfc760a3a3a09912cb546",
        "5befa9c192698d06ab415071",
        "5bf1b1a849808205ba15039a",
        "5ad5642fb5883f5b2fed7fac",
        "5bf0448692698d06ab415088",
        "5c0efc082e9ce106857709f0",
        "5c00956df58c0a1c27aba5f2",
        "5cad890845ec963c3217df93",
        "5bf2a56849808205ba150404", //Antonio
        "5bf2df57f52e110e89a417d3", //Diego Caballero
      ];
      if (
        !userId &&
        this.$store &&
        this.$store.state.user &&
        bjjlinkAdmins.indexOf(this.$store.state.user._id.toString()) >= 0
      )
        return true;
      else if (bjjlinkAdmins.indexOf(userId) >= 0) return true;
      else return false;
    },
    trimStrings(strings) {
      return strings.map((str) => str.trim());
    },
    checkExistingUser(newUser) {
      let isMinor = newUser.isMinor;
      let email = newUser.email;
      if (isMinor) {
        // Email uniqueness doesn't apply to minors (they can share a
        // guardian's email) — clear any stale duplicate-check result left
        // over from before the switch was toggled, so the add-member button
        // isn't blocked by a check that no longer applies.
        if (this.existingNewUser) this.existingNewUser = null;
        return;
      }
      if (email && email.indexOf("@") > 0) {
        this.checkingEmail = true;
        Api()
          .post(
            "admins/academy/" +
              this.$store.state.managedAcademy +
              "/check/email",
            { email: email.trim() },
          )
          .then((response) => {
            this.checkingEmail = false;
            if (response && response.data.user) {
              this.existingNewUser = response.data.user;
            } else if (response && response.data.visitor) {
              this.newUser = {};
              this.newUser.email = response.data.visitor.email;
              const split = response.data.visitor.name.split(" ");
              this.newUser.fName = split[0];
              if (response.data.visitor.isVisitor)
                this.newUser.isVisitor = true;
              else this.newUser.isProspect = true;
              if (split[1]) this.newUser.lName = split[1];
            } else if (
              this.existingNewUser ||
              this.newUser.isVisitor ||
              this.newUser.isProspect
            ) {
              this.newUser = { email: email };
              this.newUser.fName = null;
              this.newUser.isProspect = null;
              this.newUser.isVisitor = null;
              this.newUser.lName = null;
              this.existingNewUser = null;
            }
          })
          .catch(() => {
            this.checkingEmail = false;
          });
      } else if (this.existingNewUser) {
        this.existingNewUser = null;
      }
    },
    getTodaySchedule(scheduleArray) {
      if (!scheduleArray || !scheduleArray.length) return null;
      // Get today's day in lowercase, e.g., "monday"
      const today = this.getDayString(new Date());
      // Filter and sort
      return scheduleArray
        .filter((entry) => entry.day.toLowerCase() === today)
        .sort((a, b) => a.start - b.start);
    },
    navigateTo(info, fromCrumb) {
      try {
        let maxCrumbs = 8;
        if (this.isSmallerDisplay()) maxCrumbs = 5;

        let crumbs = this.$router.crumbs;
        const item = this.getBreadcrumbItem(info);

        // Check if we are navigating from a breadcrumb
        if (!fromCrumb) {
          if (
            item &&
            (crumbs.length === 0 ||
              crumbs[crumbs.length - 1].id !== item.id ||
              crumbs[crumbs.length - 1].crumbName !== item.crumbName)
          ) {
            let updatedCrumbs = crumbs.filter(
              (c) => c && (c.id !== item.id || c.crumbName !== item.crumbName),
            );
            updatedCrumbs.push(item);

            while (updatedCrumbs.length > maxCrumbs) {
              updatedCrumbs.shift();
            }

            updatedCrumbs.forEach((c, index) => {
              if (c) {
                c.disabled = index === updatedCrumbs.length - 1;
              }
            });

            crumbs.splice(0, crumbs.length, ...updatedCrumbs);
          }
        } else {
          crumbs.forEach((c) => {
            if (c) {
              c.disabled = c.crumbName === info.crumbName;
            }
          });
        }

        this.$router.push(info).catch(() => {});
        this.logActivity({
          activityType: "admin_router_" + info.name,
        });
      } catch (e) {
        this.navigateTo({ name: "home" });
      }
    },
    findMatches(text, regex) {
      let groups = [];
      text.replace(regex, (match) => {
        groups.push(match);
        return match; // We don't modify the text in this step
      });
      return groups;
    },
    replaceMatches(text, regex, replacements) {
      let index = 0;
      return text.replace(regex, () => replacements[index++] || "");
    },
    blobFromBase64(base64) {
      const base64Data = base64.replace(/^data:image\/\w+;base64,/, ""); // remove the header
      const byteCharacters = atob(base64Data);
      const byteArrays = [];

      for (let i = 0; i < byteCharacters.length; i += 512) {
        const slice = byteCharacters.slice(i, i + 512);
        const byteNumbers = new Array(slice.length)
          .fill(0)
          .map((_, j) => slice.charCodeAt(j));
        byteArrays.push(new Uint8Array(byteNumbers));
      }
      return new Blob(byteArrays, { type: "image/jpeg" }); // ✅ always JPEG
    },
    getBreadcrumbItem(data) {
      /** @param {string} s */
      const truncate = (s) =>
        s && s.length > 22 ? s.substring(0, 22) + "…" : s;
      let crumb = null;
      let route = null;
      let id = null;
      let name = null;
      if (data) {
        route = data;
        id = data.name;
        if (data.name == "calendar") {
          name = this.$t("message.calendar");
        } else if (data.name == "calendartabs") {
          let tabId = data.params ? data.params.tabId : 0;
          if (tabId == 1) {
            name = this.$t("message.events");
            id = name + "_" + 1;
          } else if (tabId == 2) {
            name = this.$t("message.schedule");
            id = name + "_" + 2;
          } else if (tabId == 3) {
            name = this.$t("message.configuration");
            id = name + "_" + 3;
          } else {
            name = this.$t("message.calendar");
            id = name + "_" + 0;
          }
        } else if (data.name == "videos") {
          name = this.$t("message.videos");
        } else if (data.name == "transactions") {
          name = this.$t("message.transactions");
        } else if (data.name == "transactionstab") {
          let tabId = data.params ? data.params.tabId : 0;
          if (tabId == 1) {
            name = this.$t("message.memberships");
            id = name + "_" + 1;
          } else if (tabId == 2) {
            name = this.$t("message.purchases");
            id = name + "_" + 2;
          } else if (tabId == 3) {
            name = this.$t("message.card_invoices");
            id = name + "_" + 3;
          } else if (tabId == 4) {
            name = this.$t("message.expenses");
            id = name + "_" + 4;
          } else {
            name = this.$t("message.transactions");
            id = name + "_" + 0;
          }
        } else if (data.name == "products") {
          name = this.$t("message.products");
        } else if (
          data.name == "automation-builder" ||
          data.name == "automations"
        ) {
          name = this.$t("message.automations");
        } else if (data.name == "managecustompages") {
          name = this.$t("message.custom_pages");
        } else if (data.name == "manageproductspackage") {
          name = this.$t("message.package");
        } else if (data.name == "manageproduct") {
          name = this.$t("message.product");
        } else if (data.name == "productstabs") {
          let tabId = data.params ? data.params.tabId : 0;
          if (tabId == 1) {
            name = this.$t("message.memberships");
            id = name + "_" + 1;
          } else if (tabId == 2) {
            name = this.$t("message.packages");
            id = name + "_" + 1;
          } else {
            name = this.$t("message.products");
            id = name + "_" + 0;
          }
        } else if (data.name == "academy") {
          name = this.$t("message.academy");
        } else if (data.name == "checkins") {
          name = this.$t("message.checkins");
        } else if (data.name == "waivers") {
          name = this.$t("message.documents");
        } else if (data.name == "classnotes") {
          name = this.$t("message.class_notes");
        } else if (data.name == "viewattendance") {
          name = this.$t("message.attendance");
        } else if (data.name == "viewattendancetabs") {
          let tabId = data.params ? data.params.tabId : 0;
          if (tabId == 1) {
            name = this.$t("message.attendance_details");
            id = name + "_" + 1;
          } else if (tabId == 2) {
            name = this.$t("message.manual_checkin");
            id = name + "_" + 2;
          } else if (tabId == 3) {
            name = this.$t("message.staff_checkins");
            id = name + "_" + 3;
          } else {
            name = this.$t("message.attendance");
            id = name + "_" + 0;
          }
        } else if (data.name == "members") {
          name = this.$t("message.members");
        } else if (data.name == "flowcharts") {
          name = this.$t("message.flowcharts");
        } else if (data.name == "growthcentral") {
          name = this.$t("message.growth");
        } else if (data.name == "growthcentraltab") {
          let tabId = data.params ? data.params.tabId : 0;
          if (tabId == 1) {
            name = this.$t("message.leads");
            id = name + "_" + 1;
          } else if (tabId == 2) {
            name = this.$t("message.visitors");
            id = name + "_" + 2;
          } else if (tabId == 3) {
            name = this.$t("message.inquiries");
            id = name + "_" + 3;
          } else if (tabId == 4) {
            name = this.$t("message.at_risk");
            id = name + "_" + 4;
          } else if (tabId == 5) {
            name = this.$t("message.referrals");
            id = name + "_" + 5;
          } else {
            name = this.$t("message.growth");
            id = name + "_" + 0;
          }
        } else if (data.name == "memberstab") {
          let tabId = data.params ? data.params.tabId : 0;
          if (tabId == 1) {
            name = this.$t("message.pending");
            id = name + "_" + 1;
          } else if (tabId == 2) {
            name = this.$t("message.alumni");
            id = name + "_" + 2;
          } else {
            name = this.$t("message.members");
            id = name + "_" + 0;
          }
        } else if (data.name == "home") {
          name = this.$t("message.home");
        } else if (data.name == "syllabusbuilder") {
          name = this.$t("message.syllabus");
        } else if (data.name == "releases") {
          name = this.$t("message.releases");
        } else if (data.name == "viewtransaction") {
          name = this.$t("message.transaction");
        } else if (data.name == "viewmember") {
          name = this.$t("message.member");
        } else if (data.name == "viewvisitor") {
          name = this.$t("message.visitor");
          // ── Plus equivalents of tabbed list routes ──────────────────────────
        } else if (data.name == "members-adminplus") {
          name = this.$t("message.members");
        } else if (data.name == "members-adminplus-tab") {
          const tabId = data.params ? data.params.tabId : 0;
          if (tabId == 1) {
            name = this.$t("message.pending");
            id = name + "_1";
          } else if (tabId == 2) {
            name = this.$t("message.alumni");
            id = name + "_2";
          } else {
            name = this.$t("message.members");
            id = name + "_0";
          }
        } else if (data.name == "transactions-adminplus") {
          name = this.$t("message.transactions");
        } else if (data.name == "transactions-adminplus-tab") {
          const tabId = data.params ? data.params.tabId : 0;
          if (tabId == 1) {
            name = this.$t("message.memberships");
            id = name + "_1";
          } else if (tabId == 2) {
            name = this.$t("message.purchases");
            id = name + "_2";
          } else if (tabId == 3) {
            name = this.$t("message.expenses");
            id = name + "_3";
          } else {
            name = this.$t("message.transactions");
            id = name + "_0";
          }
        } else if (data.name == "calendar-adminplus") {
          name = this.$t("message.calendar");
        } else if (data.name == "calendar-adminplus-tab") {
          const tabId = data.params ? data.params.tabId : 0;
          if (tabId == 1) {
            name = this.$t("message.events");
            id = name + "_1";
          } else if (tabId == 2) {
            name = this.$t("message.schedule");
            id = name + "_2";
          } else if (tabId == 3) {
            name = this.$t("message.configuration");
            id = name + "_3";
          } else {
            name = this.$t("message.calendar");
            id = name + "_0";
          }
          // ── Calendar / events ───────────────────────────────────────────────
        } else if (
          data.name == "manageacademyeventplus" ||
          data.name == "manageacademyevent"
        ) {
          name = this.$t("message.event");
        } else if (data.name == "manageacademyclass") {
          name = this.$t("message.class");
        } else if (data.name == "manageacademyeventplus") {
          name = this.$t("message.event");
          // ── Documents / waivers ─────────────────────────────────────────────
        } else if (data.name == "waiverstab") {
          name = this.$t("message.documents");
        } else if (data.name == "managedocument") {
          name = this.$t("message.document");
        } else if (
          data.name == "viewcompleteddocument" ||
          data.name == "viewsurveyanswers"
        ) {
          name = this.$t("message.document");
        } else if (data.name == "viewsurveyaggregates") {
          name = "Survey Results";
        } else if (
          data.name == "printwaiver" ||
          data.name == "printwaivertemplate" ||
          data.name == "printdocumenttemplate"
        ) {
          name = this.$t("message.document");
          // ── Products ────────────────────────────────────────────────────────
        } else if (data.name == "manageproductplus") {
          name = this.$t("message.product");
        } else if (
          data.name == "manageproductspackageplus" ||
          data.name == "manageacademypackage"
        ) {
          name = this.$t("message.package");
        } else if (data.name == "productscategory") {
          name = this.$t("message.products");
        } else if (
          data.name == "publicmemberships" ||
          data.name == "productsmemberships"
        ) {
          name = this.$t("message.memberships");
        } else if (
          data.name == "managepromocodes" ||
          data.name == "managepromocodesplus"
        ) {
          name = this.$t("message.promo_codes");
          // ── Selling ─────────────────────────────────────────────────────────
        } else if (data.name == "bulksell" || data.name == "bulksellplus") {
          name = "Bulk Sell";
        } else if (data.name == "sellamount" || data.name == "sellamountplus") {
          name = this.$t("message.sell");
        } else if (
          data.name == "sellproduct" ||
          data.name == "sellproductplus"
        ) {
          name = "Sell Product";
          // ── Transactions / payments ─────────────────────────────────────────
        } else if (data.name == "viewitemstransaction") {
          name = this.$t("message.transaction");
        } else if (data.name == "viewpayment") {
          name = "Payment";
        } else if (data.name == "viewinvoice") {
          name = this.$t("message.invoice");
        } else if (
          data.name == "manageexpense" ||
          data.name == "manageexpense-adminplus"
        ) {
          name = "Expense";
        } else if (
          data.name == "updatecard" ||
          data.name == "updatecardmember"
        ) {
          name = this.$t("message.update_card");
          // ── Media / content ─────────────────────────────────────────────────
        } else if (data.name == "managevideo") {
          name = this.$t("message.video");
        } else if (data.name == "manageflowchart") {
          name = this.$t("message.flowchart");
        } else if (data.name == "manageemailtemplate") {
          name = "Email Template";
        } else if (data.name == "websitebuilder") {
          name = "Website Builder";
          // ── Growth / members ────────────────────────────────────────────────
        } else if (data.name == "viewmemberinactiveinstances") {
          name = this.$t("message.history");
        } else if (data.name == "advancedusersearch") {
          name = this.$t("message.advanced_search");
        } else if (data.name == "referrals") {
          name = this.$t("message.referral");
        } else if (data.name == "manageaffiliate") {
          name = this.$t("message.affiliate");
        } else if (data.name == "partner-access") {
          name = "Partner Access";
        } else if (data.name == "manageacademies") {
          name = "Academies";
          // ── Academy / admin ─────────────────────────────────────────────────
        } else if (data.name == "academysettings") {
          name = "Settings";
        } else if (data.name == "academysetup") {
          name = "Academy Setup";
        } else if (data.name == "notifications") {
          name = this.$t("message.notifications");
        } else if (data.name == "schedule") {
          name = this.$t("message.schedule");
        } else if (data.name == "upgrade-plan-adminplus") {
          name = "Upgrade Plan";
        } else if (
          data.name == "payment-modules" ||
          data.name == "onboarding-infinicept"
        ) {
          name = "Payment Setup";
        } else {
          name = this.$t("message.home");
          id = name;
        }
      }
      if (route && id && name) {
        crumb = Object.assign({}, data);
        crumb.crumbName = data.crumbName ? truncate(data.crumbName) : name;
        crumb.id = id;
        crumb.disabled = true;
      }
      return crumb;
    },
    getTransactionPaymentTypeIcon(t) {
      if (t) {
        let icon = null;
        if (t.freeMembership) {
          icon = "mdi-currency-usd-off";
        } else if (
          t.lastPaymentStatusData &&
          t.lastPaymentStatusData.status.toLowerCase() == "paused"
        ) {
          icon = "mdi-pause-circle-outline";
        } else if (
          t.lastPaymentStatusData &&
          t.lastPaymentStatusData.status.toLowerCase() == "trialing"
        ) {
          icon = "mdi-timer-sand";
        } else {
          if (
            t.lastPaymentStatusData &&
            t.lastPaymentStatusData.status == "pending_payment"
          ) {
            let paymentTypes = t.paymentTypes
              ? t.paymentTypes
              : t.lastPaymentStatusData && t.lastPaymentStatusData.paymentTypes
              ? t.lastPaymentStatusData.paymentTypes
              : null;
            if (!paymentTypes && t.lastPaymentStatusData.paymentType)
              paymentTypes = [t.lastPaymentStatusData.paymentType];
            if (paymentTypes) {
              if (paymentTypes.length == 1) {
                if (paymentTypes[0] == "card") icon = "mdi-credit-card-outline";
                else icon = "mdi-bank";
              } else {
                icon = "mdi-card-multiple-outline";
              }
            }
          } else {
            let paymentType = t.paymentType
              ? t.paymentType
              : t.lastPaymentStatusData && t.lastPaymentStatusData.paymentType
              ? t.lastPaymentStatusData.paymentType
              : null;
            if (!paymentType) {
              const paymentTypes = t.paymentTypes
                ? t.paymentTypes
                : t.lastPaymentStatusData &&
                  t.lastPaymentStatusData.paymentTypes
                ? t.lastPaymentStatusData.paymentTypes
                : null;
              if (paymentTypes) {
                if (paymentTypes.length == 1) paymentType = paymentTypes[0];
                else paymentType = "multiple";
              }
            }
            if (paymentType) {
              if (paymentType == "multiple") icon = "mdi-card-multiple-outline";
              else if (paymentType == "card") icon = "mdi-credit-card-outline";
              else if (paymentType.indexOf("bank") >= 0) icon = "mdi-bank";
              else if (paymentType.indexOf("terminal") >= 0)
                icon = "mdi-calculator";
              else if (paymentType == "cash") icon = "mdi-cash-100";
            }
          }
        }
        if (!icon && t.paidMember) icon = "mdi-cash-100";
        return icon;
      } else {
        return "mdi-ticket-confirmation-outline";
      }
    },
    getBrowserLang() {
      var lang = "en";
      var blang = navigator.language || navigator.userLanguage;
      if (blang) {
        const split = blang.split("-");
        lang = split[0];
      }
      return lang;
    },

    goToTransaction(id) {
      if (id) {
        this.navigateTo({ name: "viewtransaction", params: { sessionId: id } });
      }
    },
    daysBetween(d1, d2) {
      const a = moment(d1);
      const b = moment(d2);
      return a.diff(b, "days"); // =1
    },
    //How long someone has been a member, as "2 years, 30 days".
    //Days are counted from the anniversary, not the raw remainder, so it lines
    //up with how people actually say it. Returns "" for a missing or future date.
    membershipDuration(joinedDate) {
      if (!joinedDate) return "";
      const start = moment(joinedDate).startOf("day");
      const today = moment().startOf("day");
      if (!start.isValid() || start.isAfter(today)) return "";
      const years = today.diff(start, "years");
      const days = today.diff(start.clone().add(years, "years"), "days");
      const parts = [];
      if (years > 0)
        parts.push(
          years + " " + this.$t(years === 1 ? "message.year" : "message.years"),
        );
      if (days > 0 || years === 0)
        parts.push(
          days + " " + this.$t(days === 1 ? "message.day" : "message.days"),
        );
      return parts.join(", ");
    },
    monthsBetween(d1, d2) {
      const a = moment(d1);
      const b = moment(d2);
      return a.diff(b, "months"); // =1
    },
    hoursBetween(d1, d2) {
      const date1 = new Date(d1);
      const date2 = new Date(d2);
      const differenceInMilliseconds = Math.abs(
        date2.getTime() - date1.getTime(),
      );
      const hours = differenceInMilliseconds / (1000 * 60 * 60);

      return Math.round(hours);
    },
    capitalizeText(value) {
      if (!value) return "";
      return value.replace(/\w\S*/g, function (txt) {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
      });
    },
    removeSpecialCharacters(val) {
      if (val) {
        let str = val.trim();
        str = str.replace(/\s+/g, "").toLowerCase();
        str = str.replace(/[&/\\#,+()$~%.'":*?<>{}@]/g, "");
        return str;
      }
    },
    isSameDate(d1, d2) {
      if (!d1 || !d2) return false;
      if (
        d1.getFullYear() == d2.getFullYear() &&
        d1.getDate() == d2.getDate() &&
        d1.getMonth() == d2.getMonth()
      )
        return true;
      return false;
    },
    isSmallerDisplay() {
      if (
        this.$vuetify.breakpoint.name == "xs" ||
        this.$vuetify.breakpoint.name == "sm"
      )
        return true;
      else return false;
    },
    formatNumberWithK(num) {
      if (num < 1000) return num.toString();

      const rounded = Math.floor(num / 1000);
      return `${rounded}K`;
    },
    getIntervalDisplay(interval, days) {
      if (!interval) return "";
      if (interval == "day") return this.$t("message.daily");
      else if (interval == "month") return this.$t("message.monthly");
      else if (interval == "week") return this.$t("message.weekly");
      else if (interval == "bi-week") return this.$t("message.bi_weekly");
      else if (interval == "bi-year") return this.$t("message.bi_yearly");
      else if (interval == "year") return this.$t("message.yearly");
      else if (interval == "custom-day-cycle")
        return this.$t("message.custom_day_cycle") + (days ? ": " + days : "");
      else return interval;
    },
    getOS() {
      const platform =
        window.navigator?.userAgentData?.platform ||
        window.navigator?.platform ||
        "unknown";
      let userAgent = window.navigator.userAgent,
        macosPlatforms = [
          "Macintosh",
          "MacIntel",
          "MacPPC",
          "Mac68K",
          "macOS",
          "mac",
        ],
        windowsPlatforms = ["Win32", "Win64", "Windows", "WinCE"],
        iosPlatforms = ["iPhone", "iPad", "iPod"],
        os = null;

      if (macosPlatforms.indexOf(platform) !== -1) {
        os = "Mac OS";
      } else if (iosPlatforms.indexOf(platform) !== -1) {
        os = "iOS";
      } else if (windowsPlatforms.indexOf(platform) !== -1) {
        os = "Windows";
      } else if (/Android/.test(userAgent)) {
        os = "Android";
      } else if (!os && /Linux/.test(platform)) {
        os = "Linux";
      }
      return os;
    },
    getBeltColor(belt) {
      if (belt == "white") return "#FFFFFF";
      else if (belt == "blue") return "#0075F2";
      else if (belt == "purple") return "#8D70B2";
      else if (belt == "brown") return "#50341C";
      else if (belt == "black") return "#000000";
      else if (belt == "red") return "#ED1C24";
      else return "#FFFFFF";
    },
    getCurrencySymbol(c) {
      if (!c) return "";
      if (c.toLowerCase() == "usd") return "$";
      else return c;
    },
    isDesktopTransaction(t) {
      if (t) {
        if (t.processedBy == "desk") return true;
      }
      return false;
    },
    isDarkMode() {
      return this.$vuetify.theme.dark;
    },
    getColor(c) {
      let color = "white";
      if (!c) return color;
      if (c == "light") color = "grey";
      else if (c == "dark") color = "white";
      else if (c == "gray" || c.indexOf("gray") >= 0)
        color = this.themeColor("gray");
      else if (c == "yellow" || c.indexOf("yellow") >= 0)
        color = this.themeColor("yellow");
      else if (c == "orange" || c.indexOf("orange") >= 0)
        color = this.themeColor("orange");
      else if (c == "green" || c.indexOf("green") >= 0)
        color = this.themeColor("green");
      else if (c == "blue" || c.indexOf("blue") >= 0) color = "#0075F2";
      else if (c == "purple" || c.indexOf("purple") >= 0) color = "#8D70B2";
      else if (c == "brown" || c.indexOf("brown") >= 0) color = "#50341C";
      else if (c == "black" || c.indexOf("black") >= 0) color = "black";
      else if (c == "primary") color = this.themeColor(c);
      else if (c == "bronze") color = "#cd7f32";
      else if (c == "gold") color = "#FFD700";
      else if (c == "silver") color = "#C0C0C0";
      else if (c == "red") color = this.themeColor("error");
      else if (c == "gray") color = this.themeColor(c);
      else if (c) color = c;
      return color; //#4b4b4b; gray
    },
    isCustomAchPaymentType(type) {
      if (
        type.indexOf("acss_debit") >= 0 ||
        type.indexOf("nz_becs_debit") >= 0 ||
        type.indexOf("becs_debit") >= 0
      ) {
        return true;
      }
      return false;
    },
    isAchPaymentType(type) {
      if (!type) return false;
      if (
        type.indexOf("acss_debit") >= 0 ||
        type.indexOf("nz_becs_debit") >= 0 ||
        type.indexOf("becs_debit") >= 0 ||
        type.indexOf("us_bank_account") >= 0 ||
        type.indexOf("bacs_debit") >= 0 ||
        type.indexOf("au_becs_debit") >= 0 ||
        type.indexOf("sepa_debit") >= 0 ||
        type.indexOf("ach") >= 0
      ) {
        return true;
      }
      return false;
    },
    getWhatsAppLink(countryCallingCode, phone) {
      if (!phone) {
        return null;
      }

      // If phone starts with a plus and digits (e.g. +51...)
      if (/^\+\d+/.test(phone)) {
        // Remove the plus, keep the rest
        const number = phone.replace(/\D/g, "");
        return `https://wa.me/${number}`;
      }

      //If we got here, we need the code
      if (!countryCallingCode) {
        return null;
      }

      // Remove all non-digit characters
      let cleanedPhone = phone.replace(/\D/g, "");

      // Remove leading zeros
      cleanedPhone = cleanedPhone.replace(/^0+/, "");

      // If it already starts with the country code, don't add it again
      if (cleanedPhone.startsWith(countryCallingCode)) {
        return `https://wa.me/${cleanedPhone}`;
      }

      // Prepend the country code
      const fullNumber = `${countryCallingCode}${cleanedPhone}`;

      return `https://wa.me/${fullNumber}`;
    },
    getBeltColorForProgress(belt) {
      let color = this.getColor(belt);
      if (belt && belt.indexOf("white") >= 0) color = "#D7DBDD";
      return color;
    },
    getProgressTextColor(level, linear) {
      //Linear needs to be depending on the color of the belt
      if (!level) {
        if (this.isDarkMode()) return "white";
        else return "black";
      } else {
        if (linear) {
          if (level.indexOf("black") >= 0) return "white";
          else return "black";
        } else {
          if (this.isDarkMode()) return "white";
          else return "black";
        }
      }
    },
    isMonthlyBelt(level) {
      if (level && level.indexOf("_m_") > 0) return true;
      return false;
    },
    truncate(value, limit, skipDots) {
      if (value) {
        let dots = "...";
        if (skipDots) dots = "";
        if (!limit) {
          limit = this.descriptionLimit;
          dots = "";
        }
        if (value.length > limit) {
          value = value.substring(0, limit - 3) + dots;
        }
        return value;
      }
    },
    showAlert(type, title, text, time, position) {
      let width = "60vw";
      if (this.isSmallerDisplay()) width = "90vw";
      this.$swal.fire({
        position: position ? position : "bottom",
        icon: type, // warning, error, success, info, and question
        title: title,
        toast: true,
        text: text,
        timer: time ? time : 5000,
        timerProgressBar: true,
        width: width,
        showConfirmButton: false,
        customClass: {
          popup: "sweet-alert-light",
        },
      });
    },
    getInitials(name) {
      if (!name) return "";
      const names = name.trim().split(" ");
      if (names.length >= 2) {
        return (
          names[0].charAt(0) + names[names.length - 1].charAt(0)
        ).toUpperCase();
      }
      return name.charAt(0).toUpperCase();
    },

    getQueryParams(params) {
      var queryParams = "?";
      if (params) {
        for (const prop in params)
          queryParams =
            queryParams + "&" + prop + "=" + encodeURIComponent(params[prop]);
      }
      return queryParams;
    },
    goBack() {
      window.history.length > 1
        ? this.$router.go(-1)
        : this.navigateTo({ name: "home" });
    },
    getWeekDays() {
      return [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ];
    },
    hourInNumberToDate(time, date) {
      const fixed = parseFloat(time).toFixed(2);
      const [h, m] = fixed.split(".");
      const d = date ? new Date(date) : new Date();
      d.setHours(parseInt(h, 10));
      d.setMinutes(parseInt(m || "0", 10));
      d.setSeconds(0);
      return d;
    },
    dayToNumber(day) {
      if (day == "monday") return 1;
      else if (day == "tuesday") return 2;
      else if (day == "wednesday") return 3;
      else if (day == "thursday") return 4;
      else if (day == "friday") return 5;
      else if (day == "saturday") return 6;
      else return 0;
    },
    isRoot() {
      const permissions = this.$store.state.permissions;
      if (permissions && permissions.indexOf("root") >= 0) return true;
      else return false;
    },
    daysUntilBirthDate(dob) {
      if (!dob) {
        return 1000;
      } else {
        let today = new Date();
        today.setMinutes(0);
        today.setHours(0);
        let d2 = this.dateFromString(dob);
        if (d2) {
          d2.setFullYear(today.getFullYear());
          let differenceInTime = d2.getTime() - today.getTime();
          let differenceInDays =
            -1 * parseInt(Math.round(differenceInTime / (1000 * 3600 * 24)));
          return differenceInDays;
        }
        return 1000;
      }
    },
    roundNumber(num, decimals) {
      const decimalPlaces = Math.max(0, parseInt(decimals, 10) || 0);

      // Validate the number. If invalid, return a formatted "0".
      const numericVal = parseFloat(num);
      if (!isFinite(numericVal)) {
        return (0).toFixed(decimalPlaces);
      }

      // Return the string directly from .toFixed()
      return numericVal.toFixed(decimalPlaces);
    },
    getAge(dob) {
      if (!dob) {
        return 0;
      } else {
        let date = this.dateFromString(dob);
        return moment().diff(date, "years");
      }
    },
    capitalizeFirstLetter(val) {
      if (!val) return;
      return String(val).charAt(0).toUpperCase() + String(val).slice(1);
    },
    setLang() {
      const browserLang = navigator.language;
      if (this.$store && this.$store.state && !this.$store.state.language) {
        var newLang = "en;";
        if (browserLang) {
          if (browserLang.indexOf("es") >= 0) newLang = "es";
          else if (browserLang.indexOf("fr") >= 0) newLang = "fr";
          else if (browserLang.indexOf("pt") >= 0) newLang = "pt";
        }
        this.$i18n.locale = newLang;
      } else {
        this.$i18n.locale = this.$store.state.language;
      }
    },
    userFile(path) {
      //Replace with versionn 2: return process.env.API_FILES_HOST + img;
      if (!path || path == "") return this.assets("default-profile-avatar.png");
      else if (path.indexOf("https:") == 0) return path;
      else if (path.indexOf("userfiles") >= 0)
        return Environment.get("VUE_APP_API_HOST") + "/api/" + path;
      else return Environment.get("VUE_APP_API_HOST") + "/api/" + path;
    },
    numberToTime(time) {
      if ((!time && time !== 0) || isNaN(time)) return null;
      const fixed = parseFloat(time).toFixed(2);
      const [h, m] = fixed.split(".");
      return `${h}:${m || "00"}`;
    },
    numberToHoursAndMinutes(n) {
      if (!n && n !== 0) return null;
      const fixed = parseFloat(n).toFixed(2);
      const [h, m] = fixed.split(".");
      return { hours: parseInt(h, 10), minutes: parseInt(m || "0", 10) };
    },
    parseLocalDate(input) {
      let dateString;

      if (input instanceof Date) {
        // Convert Date object to local date-time string
        // Example: "2025-05-26T14:00:00"
        const year = input.getFullYear();
        const month = String(input.getMonth() + 1).padStart(2, "0");
        const day = String(input.getDate()).padStart(2, "0");
        const hour = String(input.getHours()).padStart(2, "0");
        const minute = String(input.getMinutes()).padStart(2, "0");
        const second = String(input.getSeconds()).padStart(2, "0");

        dateString = `${year}-${month}-${day}T${hour}:${minute}:${second}`;
      } else if (typeof input === "string") {
        dateString = input;
      } else {
        throw new Error("Input must be a string or Date object");
      }

      // Now parse as local time, ignoring timezone conversion
      const [datePart, timePart] = dateString.split("T");
      const [year, month, day] = datePart.split("-").map(Number);
      const [hour, minute, second] = timePart.split(":").map(Number);

      return new Date(year, month - 1, day, hour, minute, second || 0);
    },
    userImg(img, logoImage) {
      //Replace with versionn 2: return process.env.API_FILES_HOST + img;
      if (img && img != "") {
        if (img.indexOf("https:") == 0) return img;
        else if (img.indexOf("api") == 0)
          return (
            Environment.get("VUE_APP_API_HOST") +
            "/api/" +
            encodeURIComponent(img)
          );
        else if (img.indexOf("userfiles") >= 0)
          return Environment.get("VUE_APP_API_HOST") + "/api/" + img;
        else if (img.indexOf("media") >= 0)
          return Environment.get("VUE_APP_API_HOST") + "/api/" + img;
      } else if (!logoImage) {
        return (
          Environment.get("VUE_APP_ASSETS_HOME") + "/default-profile-avatar.png"
        );
      } else {
        return Environment.get("VUE_APP_ASSETS_HOME") + "/logo-circle.png";
      }
    },
    getDayString(day) {
      let weekdays = new Array(7);
      weekdays[0] = "sunday";
      weekdays[1] = "monday";
      weekdays[2] = "tuesday";
      weekdays[3] = "wednesday";
      weekdays[4] = "thursday";
      weekdays[5] = "friday";
      weekdays[6] = "saturday";
      return weekdays[day.getDay()];
    },
    adjustTimeZone(date, offset, revert) {
      let multiplier = revert ? -1 : 1;
      if (offset != 0) {
        date.setHours(date.getHours() + multiplier * offset);
        return date;
      }
      return date;
    },
    toNiceDateCustomFormat(d, format) {
      return moment.utc(d).format(format);
    },
    toNiceDateCustomFormatAdjusted(d, format) {
      return moment(d).format(format);
    },
    forceDateToUTC(date) {
      return new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
      );
    },
    convertUtcToZonedDate(utcString, timeZone) {
      const utcDate = new Date(utcString);

      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

      const parts = formatter.formatToParts(utcDate).reduce((acc, part) => {
        if (part.type !== "literal") acc[part.type] = part.value;
        return acc;
      }, {});

      const localISO = `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:00`;
      return new Date(localISO);
    },
    adjustToTimeZone(date, timeZone) {
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const localOffset = -1 * (new Date().getTimezoneOffset() / 60);
      //console.log('Local offset: ', localOffset);
      if (timeZone != browserTimezone) {
        const timeZoneOffset = 6;
        //console.log('Event offset: ', event.utcOffset);
        const diff = -1 * localOffset + timeZoneOffset;
        //console.log('DIFF: ', diff);
        if (diff < 0) {
          //Add different to times and be done
          date.setHours(date.getUTCHours() + diff);
        } else {
          date.setHours(date.getHours() + diff);
        }
      }
    },
    hideAlertMessage() {},
    orderedBelts() {
      return [
        "gray",
        "yellow",
        "orange",
        "green",
        "white",
        "blue",
        "purple",
        "brown",
        "black",
        "red",
      ];
    },
    getTimeAndDateInString(dateString) {
      if (dateString) {
        if (dateString instanceof Date) {
          const dateObj = dateString;
          let monthString = "" + (dateObj.getMonth() + 1);
          if (monthString.length == 1) monthString = "0" + monthString;
          const date =
            dateObj.getFullYear() + "-" + monthString + "-" + dateObj.getDate();
          let minutesString = "" + dateObj.getMinutes();
          if (minutesString.length == 1) minutesString = minutesString + "0";
          return {
            time: dateObj.getHours() + ":" + minutesString,
            date: date,
          };
        } else {
          const split = dateString.split("T");
          if (split.length == 2)
            return { time: split[1].substring(0, 5), date: split[0] };
        }
      }
      return null;
    },
    getBrowserOffset() {
      const d = new Date();
      const offsetMin = d.getTimezoneOffset();
      if (offsetMin != 0) return new Number(-(offsetMin / 60)).valueOf();
      else return 0;
    },
    langToLocale(lang) {
      if (lang == "pt") return "es-ES";
      else if (lang == "pt") return "pt-BR";
      else return "en-US";
    },
    decodeBase64(text) {
      let decoded = atob(text);
      return decoded;
    },
    openQR(url, sp) {
      const baseUrl = Environment.get("VUE_APP_API_HOST");
      const urlToCopy = baseUrl + url;
      if (this.isSmallerDisplay()) {
        this.copyToClipboard(urlToCopy + (sp ? "?sp=" + sp : ""));
      } else {
        let buff = new Buffer(urlToCopy);
        const encodedUrl = buff.toString("base64");
        window.open(
          Environment.get("VUE_APP_ASSETS_HOME") +
            "secure/qr/" +
            encodeURI(encodedUrl) +
            (sp ? "?sp=" + sp : ""),
        );
      }
    },
    openWhatsApp(url) {
      if (!url) return;
      window.open(url);
    },
    getTimezoneOffset(timeZone) {
      if (!timeZone) return 0;
      var currentTimeAtCityString = new Date().toLocaleString("en-US", {
        timeZone: timeZone,
      });
      var timeAtCity = new Date(currentTimeAtCityString);
      const here = new Date();
      const hoursHere = here.getHours();
      const hoursTimezone = timeAtCity.getHours();
      if (timeAtCity < here) {
        if (hoursTimezone <= hoursHere) return hoursTimezone - hoursHere;
        else return hoursTimezone - (hoursHere + 24);
      } else {
        if (hoursTimezone >= hoursHere) return hoursTimezone - hoursHere;
        else return hoursTimezone - (hoursHere - 24);
      }
    },
    centsToAmount(s) {
      if (!s) return 0;
      const amount = s + "";
      if (amount.includes(".")) {
        return s;
      }
      if (s.length <= 2) {
        let n = new Number("0." + s);
        return n.valueOf();
      } else {
        const price = amount.substring(0, amount.length - 2);
        const priceDecimal = amount.substring(amount.length - 2, amount.length);
        const n = new Number(price + "." + priceDecimal);
        return n.valueOf();
      }
    },
    encodeBase64(text) {
      let encoded = btoa(text);
      return encoded;
    },
    embedVideoUrl(url) {
      // Regular expressions to extract video ID from various YouTube URL formats
      const patterns = [
        /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/, // Standard URL
        /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/, // Embed URL
        /(?:https?:\/\/)?(?:www\.)?youtube\.com\/v\/([a-zA-Z0-9_-]{11})/, // /v/ URL
        /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]{11})/, // Shortened URL
      ];

      // Loop through patterns and try to match the video ID
      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match && match[1]) {
          return `https://www.youtube.com/embed/${match[1]}`; // Return embed URL with the extracted ID
        }
      }

      // If no match, return null or an error message
      return null; // Or throw new Error("Invalid YouTube URL");
    },
    getEditorToolbar(hideInserts) {
      let inserts = ["link", "image", "video"];
      if (hideInserts) inserts = [];
      const toolbar = [
        [{ font: [] }],

        [{ header: [false, 1, 2, 3, 4, 5, 6] }],

        [{ size: ["small", false, "large", "huge"] }],

        ["bold", "italic", "underline", "strike"],

        [
          { align: "" },
          { align: "center" },
          { align: "right" },
          { align: "justify" },
        ],

        [{ header: 1 }, { header: 2 }],

        ["blockquote", "code-block"],

        [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],

        [{ script: "sub" }, { script: "super" }],

        [{ indent: "-1" }, { indent: "+1" }],

        [{ color: [] }, { background: [] }],

        inserts,
      ];
      return toolbar;
    },
  },
});

const dateTimeFormats = {
  en: {
    time: {
      hour: "numeric",
      minute: "numeric",
    },
    month_day_year: {
      year: "numeric",
      month: "short",
      day: "numeric",
    },
    month_day: {
      month: "short",
      day: "numeric",
    },
    month_year: {
      month: "short",
      year: "numeric",
    },
    short: {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    },
    short_year: {
      month: "short",
      day: "numeric",
      year: "numeric",
    },
    long: {
      year: "numeric",
      month: "short",
      day: "numeric",
      weekday: "short",
      hour: "numeric",
      minute: "numeric",
    },
  },
  fr: {
    time: {
      hour: "numeric",
      minute: "numeric",
    },
    month_day: {
      month: "short",
      day: "numeric",
    },
    month_year: {
      month: "long",
      year: "numeric",
    },
    month_day_year: {
      year: "numeric",
      month: "short",
      day: "numeric",
    },
    short: {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    },
    short_year: {
      month: "short",
      day: "numeric",
      year: "numeric",
    },
    long: {
      year: "numeric",
      month: "short",
      day: "numeric",
      weekday: "short",
      hour: "numeric",
      minute: "numeric",
    },
  },
  es: {
    time: {
      hour: "numeric",
      minute: "numeric",
    },
    month_day_year: {
      year: "numeric",
      month: "short",
      day: "numeric",
    },
    month_day: {
      month: "short",
      day: "numeric",
    },
    month_year: {
      month: "long",
      year: "numeric",
    },
    short: {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    },
    short_year: {
      month: "short",
      day: "numeric",
      year: "numeric",
    },
    long: {
      year: "numeric",
      month: "short",
      day: "numeric",
      weekday: "short",
      hour: "numeric",
      minute: "numeric",
    },
  },
  pt: {
    time: {
      hour: "numeric",
      minute: "numeric",
    },
    month_day_year: {
      year: "numeric",
      month: "short",
      day: "numeric",
    },
    month_day: {
      month: "short",
      day: "numeric",
    },
    month_year: {
      month: "long",
      year: "numeric",
    },
    short: {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    },
    short_year: {
      month: "short",
      day: "numeric",
      year: "numeric",
    },
    long: {
      year: "numeric",
      month: "short",
      day: "numeric",
      weekday: "short",
      hour: "numeric",
      minute: "numeric",
    },
  },
};

Vue.component("FlowchartBlock", FlowchartBlock);
Vue.component("FlowchartNode", FlowchartNode);
Vue.component("FlowchartNodeView", FlowchartNodeView);
Vue.component("ImageDisplay", ImageDisplay);

const i18n = new VueI18n({
  locale: "en", // set locale
  fallbackLocale: "en",
  messages, // set locale messages
  dateTimeFormats,
  silentTranslationWarn: true,
});

sentry(Vue);

new Vue({
  router,
  store,
  vuetify,
  i18n,
  render: (h) => h(App),
}).$mount("#app");
